import { FrontendApplication, FrontendApplicationContribution } from '@theia/core/lib/browser';
import { injectable } from '@theia/core/shared/inversify';
import { ONLINE_PREVIEW_HOST_ACTIVE_CHANGED_EVENT } from './online-preview-host-widget';

const ONLINE_PREVIEW_GATE_BODY_CLASS: string = 'lvgl-online-preview-gate-active';
const APP_SHELL_SELECTOR: string = '#theia-app-shell';

type OnlinePreviewHostActiveChangedDetail = {
    active?: boolean;
};

// Keeps the Theia shell inaccessible while the browser-only Online Preview auth,
// launcher, or loading overlay is visible. Once a GitHub workspace is loaded,
// the gate is removed and the normal editor shell becomes interactive again.
@injectable()
export class OnlinePreviewShellGateContribution implements FrontendApplicationContribution {
    async onStart(_app: FrontendApplication): Promise<void> {
        this.setGateActive(true);
        window.addEventListener(ONLINE_PREVIEW_HOST_ACTIVE_CHANGED_EVENT, this.handleHostActiveChanged);
    }

    onStop(_app: FrontendApplication): void {
        window.removeEventListener(ONLINE_PREVIEW_HOST_ACTIVE_CHANGED_EVENT, this.handleHostActiveChanged);
        this.setGateActive(false);
    }

    protected readonly handleHostActiveChanged = (event: Event): void => {
        const detail: OnlinePreviewHostActiveChangedDetail | undefined = (
            event as CustomEvent<OnlinePreviewHostActiveChangedDetail>
        ).detail;
        this.setGateActive(detail?.active === true);
    };

    protected setGateActive(active: boolean): void {
        document.body.classList.toggle(ONLINE_PREVIEW_GATE_BODY_CLASS, active);
        const appShell: HTMLElement | null = document.querySelector<HTMLElement>(APP_SHELL_SELECTOR);
        if (!appShell) return;

        if (active) {
            appShell.setAttribute('inert', '');
            appShell.setAttribute('aria-hidden', 'true');
            return;
        }

        appShell.removeAttribute('inert');
        appShell.removeAttribute('aria-hidden');
    }
}
